import type { AxiosRequestConfig } from 'axios';
import type { BoneSources, PlannerState } from '@/views/3d-planner/store/plannerState';
import type { Url } from '@/domain/types';
import { type ApiCase, getCase } from '@/api/case';
import { type ApiStudy, getStudy, studyIdFromUrl } from '@/api/study/study';
import { assert, assertDefined } from '@/util/assert';
import { taggedLogger } from '@/util/taggedLogger';
import { type ApiBoneId, findStudyModel, getStudyModels } from '@/api/study/studyModels';
import {
    type ApiStudyLandmarks,
    getLandmarks,
    landmarksIdFromUrl,
} from '@/api/study/studyLandmarks';
import {
    type ApiStudyMeasurements,
    getStudyMeasurements,
    studyMeasurementsIdFromUrl,
} from '@/api/study/studyMeasurements';
import {
    type ApiCompleteSpecification,
    getCompleteSpecification,
    specificationIdFromUrl,
} from '@/views/3d-planner/api/specification';
import { getComponentCatalog } from '@/api/catalog/getComponentCatalog';
import { getFittedComponents } from '@/api/fittedComponents/getFittedComponents';
import { pollTemplate } from '@/api/template/pollTemplate';
import { anatomicAngles } from '@/domain/anatomy/pelvis/acetabularAngles';
import { toRadians } from '@/domain/anatomy/pelvis/anteversionInclination';
import type { Template } from '@/domain/template/template';
import { createManualTemplate } from '@/views/3d-planner/template/createManualTemplate';
import { templateId } from '@/api/template/templateUrl';
import { getPlan, planIdFromUrl } from '@/views/3d-planner/api/plan';
import {
    calculateSceneCentre,
    findFemoralFeatures,
    findLtResectionPoint,
    makeFemurDiceScores,
    makeNativeMeasurements,
} from './studyDerivations';
import { type ApiCaseCatStacks, fetchCatStacks } from '@/api/catStacks';
import { catstacksData } from '@/api/catstacksData';
import { computeBearingUrl } from '@/views/3d-planner/fitting/componentUrls';
import { getTemplate } from '@/api/template/getTemplate';
import { type ApiStudyFitting, fittingIdFromUrl, getFitting } from '@/api/study/studyFitting';
import { alignmentCS, AlignmentMode } from '@/domain/anatomy/pelvis/alignment';

const log = taggedLogger('case-load');

export async function loadCase(
    state: PlannerState,
    caseId: number,
    config?: AxiosRequestConfig,
): Promise<void> {
    const data = await loadCaseData(caseId, config);

    applyCaseData(state, caseId, data);

    log.info('Resolving anatomical model sources');
    state.boneSources = await resolveBoneSources(data.study, config);

    if (state.catalog) {
        log.info('Components catalog is already loaded');
    } else {
        log.info('Loading components catalog');
        state.catalog = await getComponentCatalog(config);
    }

    await initStateFromTemplate(state, data.template);

    applyCatstacks(state, data.study, data.catstacks);

    log.info('Finished loading case');
}

/**
 * Projects already-fetched data onto the planner state, which is what keeps the derivations
 * reachable without a network.
 */
export function applyCaseData(state: PlannerState, caseId: number, data: ApiData): void {
    const {
        case_,
        landmarks,
        measurements,
        fit_results,
        specification,
        study,
        autoTemplate,
        template,
        userPlanTemplate,
    } = data;

    state.case = {
        caseId: caseId,
        caseUrl: case_.self,
        caseVersion: case_.version,
        operationalSide: case_.side,
        sourceModality: study.sourceModality,
        autoTemplate: autoTemplate,
        manualTemplateUrl: case_.manualTemplate,
        userPlanTemplate,
        manualPlanApprovalState: template.currentPlanApprovalState ?? null,
        specification: specification,
    };

    state.alignmentCS = alignmentCS(specification.alignmentMode, measurements.groups);
    state.appCS = alignmentCS(AlignmentMode.APP, measurements.groups);

    state.sceneCentre = calculateSceneCentre(landmarks);
    state.nativeMeasurements = makeNativeMeasurements(
        case_.side,
        measurements,
        state.alignmentCS.toLpsVectors(),
    );
    state.diceScores = makeFemurDiceScores(fit_results);
    state.femoralFeatures = findFemoralFeatures(case_.side, measurements);
    state.ltResectionPoint = findLtResectionPoint(case_.side, measurements);
}

/** An xray case has no catstack planes; a CT case that arrived without them is a data error. */
export function applyCatstacks(
    state: PlannerState,
    study: ApiStudy,
    catstacks: ApiCaseCatStacks | null,
): void {
    if (study.sourceModality === 'ct-scan') {
        assert(catstacks !== null);
        state.catstacksData = catstacksData(catstacks);
    }
}

/** Reload the manual-template and initialise the planner-state from it */
export async function reloadTemplate(
    state: PlannerState,
    config?: AxiosRequestConfig,
): Promise<void> {
    assert(state.case, 'Cannot reload template: no case loaded');

    log.info('Reloading surgical-template %d', templateId(state.case.manualTemplateUrl).template);
    const template = await pollTemplate(
        state.case.manualTemplateUrl,
        (template) => !!template.components,
        config,
    );
    await initStateFromTemplate(state, template, config);
}

/** Initialise the planner-state from a template */
async function initStateFromTemplate(
    state: PlannerState,
    template: Template,
    config?: AxiosRequestConfig,
): Promise<void> {
    log.info('Loading fitted components');
    assert(template.components !== null);
    state.fittedComponents = await getFittedComponents(template.components, config);

    log.info('Assigning template');
    state.targetAdjustments = {
        legLength:
            template.targetLegLengthChange ??
            assertDefined(state.case?.specification.target.leg_length_change, 'no case leg length'),
        offset:
            template.targetOffsetChange ??
            assertDefined(state.case?.specification.target.offset_change, 'no case offset'),
    };
    state.template = {
        cupUrl: template.cup,
        linerUrl: template.liner,
        bearingUrl: null,
        stemUrl: template.stem,
        headUrl: template.head,
        cupRotation: anatomicAngles(toRadians(template.cupRotation)),
        cupOffset: template.cupOffset,
        dualMobility: template.dualMobility,
        stemTransform: template.stemTransform,
        targets: {
            legLength: template.targetLegLengthChange,
            offset: template.targetOffsetChange,
        },
    };
    state.template.bearingUrl = template.dualMobility ? computeBearingUrl(state) : null;
}

/** Api-data that we load up-front */
export type ApiData = {
    case_: ApiCase;
    specification: ApiCompleteSpecification;
    autoTemplate: Template;
    template: Template;

    /**
     * The template the user plan was made from, or null when the case has no user plan.
     *
     * The content, not its URL. A revert writes a new template version holding identical values,
     * so a URL can only answer "is this the same version", which is not what the approve button
     * asks - it asks whether the plan still reflects the template. See `planBaselineTemplate`.
     */
    userPlanTemplate: Template | null;
    study: ApiStudy;
    measurements: ApiStudyMeasurements;
    landmarks: ApiStudyLandmarks;
    fit_results: ApiStudyFitting;
    catstacks: ApiCaseCatStacks | null;
};

/** Up-front load of data */
async function loadCaseData(caseId: number, config?: AxiosRequestConfig): Promise<ApiData> {
    log.info('Loading case %d', caseId);
    let case_ = await getCase(caseId, config);

    const autoTemplate = await getTemplate(case_.automatedTemplate, config);

    if (!case_.manualTemplate) {
        case_ = await createManualTemplate(case_, config);
    }

    log.info(
        'Loading surgical-specification %d',
        specificationIdFromUrl(case_.specification).specification,
    );
    const specification = await getCompleteSpecification(case_.specification, config);

    log.info('Loading surgical-template %d', templateId(case_.manualTemplate).template);

    // Fetch the surgical-template until the components-url is available
    const template = await pollTemplate(
        case_.manualTemplate,
        (template) => !!template.components,
        config,
    );

    // Only the user plan's template has to be fetched; the Formus plan is made from
    // `autoTemplate`, which is already loaded. `planBaselineTemplate` picks between them.
    let userPlanTemplate = null;
    if (template.plan) {
        log.info('Loading plan %d', planIdFromUrl(template.plan).plan);
        const plan = await getPlan(template.plan, config);
        // The immutable history row the plan was made from. A legacy plan predating
        // surgical_template_history has no `up` link, and reads as no user plan.
        userPlanTemplate = plan.template ? await getTemplate(plan.template, config) : null;
    }

    log.info('Loading study %d', studyIdFromUrl(template.study).study);
    const study = await getStudy(template.study, config);

    log.info(
        'Loading study-measurements %d',
        studyMeasurementsIdFromUrl(study.measurements).measurements,
    );
    const measurements = await getStudyMeasurements(study.measurements, config);

    log.info('Loading landmarks %d', landmarksIdFromUrl(study.landmarks).landmarks);
    const landmarks = await getLandmarks(study.landmarks, config);

    log.info('Loading fit results %d', fittingIdFromUrl(study.fitting).fit_results);
    const fit_results = await getFitting(study.fitting, config);

    let catstacks: ApiCaseCatStacks | null;
    if (study.sourceModality === 'ct-scan') {
        log.info('Loading catstacks %d', landmarksIdFromUrl(study.landmarks).landmarks);
        catstacks = await fetchCatStacks(caseId, config);
        if (catstacks === null) {
            throw new Error(`Failed to load cat-stacks`);
        }
    } else {
        catstacks = null;
    }

    return {
        case_,
        specification,
        study,
        measurements,
        landmarks,
        fit_results,
        autoTemplate,
        template,
        userPlanTemplate,
        catstacks,
    };
}

/**
 * Resolve the geometry-source id for each anatomical bone. The result is stored as plain domain
 * data (`state.boneSources`); the scene ViewModel projects it onto scene nodes via
 * `bindBoneSources`, keeping the domain load free of any scene dependency.
 *
 * Required bones throw if their model is missing (a data error worth surfacing loudly); the metal
 * implant is optional and resolves to `null` when absent.
 */
export async function resolveBoneSources(
    study: ApiStudy,
    config?: AxiosRequestConfig,
): Promise<BoneSources> {
    const boneModels = await getStudyModels(study.models, config);
    const required = (bone: ApiBoneId): Url => findStudyModel(boneModels, bone).id;

    return {
        'operative-femur': required('operative-femur'),
        'operative-femur-internal': required('operative-femur-internal'),
        'operative-hemipelvis': required('operative-hemipelvis'),
        sacrum: required('sacrum'),
        'contralateral-hemipelvis': required('contralateral-hemipelvis'),
        'contralateral-femur': required('contralateral-femur'),
        metal: findStudyModel(boneModels, 'metal', { optional: true })?.id ?? null,
    };
}
